'use client';

import { useState } from 'react';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import {
  alpha,
  Box,
  Button,
  Menu,
  MenuItem,
  Paper,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';

import { useT } from '@/hooks';

export interface FilterOption {
  label: string;
  value: string;
}

interface FilterMenuItemProps {
  option: FilterOption;
  isSelected: boolean;
  onSelect: (value: string) => void;
}

function FilterMenuItem({ option, isSelected, onSelect }: FilterMenuItemProps) {
  const theme = useTheme();
  const { t } = useT();

  const handleItemClick = () => {
    onSelect(option.value);
  };

  return (
    <MenuItem
      onClick={handleItemClick}
      selected={isSelected}
      sx={{
        fontSize: '0.9rem',
        fontWeight: isSelected ? 600 : 400,
        py: 1,
        '&.Mui-selected': {
          bgcolor: alpha(theme.palette.primary.main, 0.08),
          '&:hover': {
            bgcolor: alpha(theme.palette.primary.main, 0.12),
          },
        },
      }}
    >
      {t(`filter.${option.label}`, { defaultValue: option.label })}
    </MenuItem>
  );
}

interface FilterDropdownProps {
  label?: string;
  value: string;
  options: FilterOption[];
  onChange: (value: string) => void;
  minWidth?: number | string;
  height?: number | string;
  align?: 'left' | 'right';
}

export function FilterDropdown({
  label,
  value,
  options,
  onChange,
  minWidth = 140,
  height = 34,
  align: alignProp,
}: FilterDropdownProps) {
  const theme = useTheme();
  const { t } = useT();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const align = alignProp !== undefined ? alignProp : isMobile ? 'left' : 'right';
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleSelect = (newValue: string) => {
    onChange(newValue);
    handleClose();
  };

  const selectedOption = options.find((o) => o.value === value) || options[0];

  const renderMenuItems = (option: FilterOption) => (
    <FilterMenuItem
      key={option.value}
      option={option}
      isSelected={option.value === value}
      onSelect={handleSelect}
    />
  );

  const menuItems = options.map(renderMenuItems);

  return (
    <Box sx={{ position: 'relative', width: 'fit-content' }}>
      <Button
        id={`filter-button-${label || 'dropdown'}`}
        aria-controls={open ? `filter-menu-${label || 'dropdown'}` : undefined}
        aria-haspopup="true"
        aria-expanded={open ? 'true' : undefined}
        variant="outlined"
        disableElevation
        onClick={handleClick}
        endIcon={<KeyboardArrowDownIcon sx={{ fontSize: '1.1rem !important' }} />}
        sx={{
          textTransform: 'none',
          minWidth: minWidth,
          height: height,
          borderRadius: '17px',
          padding: '4px 14px',
          borderColor:
            theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.12)' : 'rgba(0, 0, 0, 0.12)',
          color: theme.palette.text.primary,
          bgcolor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'white',
          justifyContent: 'space-between',
          '&:hover': {
            bgcolor:
              theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.04)',
            borderColor: theme.palette.text.secondary,
          },
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, overflow: 'hidden' }}>
          {label && (
            <Typography
              sx={{
                fontSize: '0.8125rem',
                color: 'text.secondary',
                whiteSpace: 'nowrap',
                fontWeight: 400,
              }}
            >
              {t(`filter.${label}`, { defaultValue: label })}
            </Typography>
          )}
          <Typography
            sx={{
              fontSize: '0.8125rem',
              fontWeight: 600,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {t(`filter.${selectedOption?.label}`, { defaultValue: selectedOption?.label })}
          </Typography>
        </Box>
      </Button>

      <Menu
        id={`filter-menu-${label || 'dropdown'}`}
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        MenuListProps={{
          'aria-labelledby': `filter-button-${label || 'dropdown'}`,
        }}
        PaperProps={{
          sx: {
            borderRadius: '12px',
            minWidth: Math.max(typeof minWidth === 'number' ? minWidth : 140, 140),
            border: '1px solid',
            borderColor: 'divider',
            bgcolor: 'background.paper',
            boxShadow: '0px 10px 30px rgba(0,0,0,0.5)',
            mt: 1,
            maxHeight: { xs: '200px', sm: '260px', md: '60vh' },
          },
        }}
        transformOrigin={{ horizontal: align, vertical: 'top' }}
        anchorOrigin={{ horizontal: align, vertical: 'bottom' }}
      >
        {menuItems}
      </Menu>
    </Box>
  );
}
